<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useBookingOptionSelectionStore } from '@/@vxapp/Products/BookingOptions/Selections/useBookingOptionSelectionStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import BookingOptionSelectionDialog from '@/components/Dialogs/BookngOptionSelections/BookingOptionSelectionDialog.vue'
import SelectionListToolbar from '@/components/ServiceLists/BookingOptionSelections/SelectionListToolbar.vue'
import BookingOptionSelectionsDatatable from '@/components/Tables/BookingOptionSelections/BookingOptionSelectionsDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type SelectionData = Record<string, unknown>
type DialogDisplayTypeValue = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]
type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search'

definePage({ meta: PageMeta.Selections.List.Meta })
setPageTitle(PageMeta.Selections.List.Title)

const log = createLogger('BookingOptionSelectionsListPage')
const route = useRoute()
const router = useRouter()
const selectionStore = useBookingOptionSelectionStore() as any

const selectionsDataTable =
  useTemplateRef<InstanceType<typeof BookingOptionSelectionsDatatable>>('selectionsDataTable')

const selectionData = ref<SelectionData | null>(null)
const dialogDisplayType = shallowRef<DialogDisplayTypeValue>(DialogDisplayType.CREATE)
const isDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)
const defaultQueryParams: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search']

const normalizeValue = (value: unknown): string | undefined => {
  const normalized = Array.isArray(value) ? value[0] : value

  if (typeof normalized === 'number') return String(normalized)

  return typeof normalized === 'string' ? normalized : undefined
}

const normalizeQueryParams = (source: Record<string, unknown>): PaginationQueryParams => {
  const page = Number.parseInt(normalizeValue(source.page) ?? '', 10)
  const limit = Number.parseInt(normalizeValue(source.limit) ?? '', 10)
  const sort = normalizeValue(source.sort)
  const search = normalizeValue(source.search)?.trim()

  return {
    ...defaultQueryParams,
    page: Number.isInteger(page) && page > 0 ? page : defaultQueryParams.page,
    limit:
      limit === -1 || (Number.isInteger(limit) && limit > 0) ? limit : defaultQueryParams.limit,
    sort: sort === '+id' || sort === '-id' ? sort : defaultQueryParams.sort,
    search: search || undefined,
  }
}

const queryParams = computed<PaginationQueryParams>(() =>
  normalizeQueryParams(selectionStore.queryParams ?? defaultQueryParams)
)

const queryParamsMatch = (left: PaginationQueryParams, right: PaginationQueryParams) =>
  left.page === right.page &&
  left.limit === right.limit &&
  left.sort === right.sort &&
  left.search === right.search

const applyRouteQuery = () => {
  const currentQuery = normalizeQueryParams(selectionStore.queryParams ?? defaultQueryParams)
  const routeQuery = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  selectionStore.queryParams = { ...selectionStore.queryParams, ...routeQuery }

  return true
}

const buildRouteQuery = (
  source: Record<string, unknown> = selectionStore.queryParams ?? defaultQueryParams
) => {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  const queryParams = normalizeQueryParams(source)

  return {
    ...nextQuery,
    ...(queryParams.page !== defaultQueryParams.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== defaultQueryParams.limit ? { limit: String(queryParams.limit) } : {}),
    ...(queryParams.sort !== defaultQueryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
  }
}

const syncRouteQuery = async (queryParams: PaginationQueryParams) => {
  const nextQuery = buildRouteQuery({ ...queryParams })

  if (JSON.stringify(route.query) !== JSON.stringify(nextQuery))
    await router.replace({ query: nextQuery })
}

const fetchSelections = async () => {
  await selectionsDataTable.value?.fetchBookingOptionSelections()
}

const updateQueryParams = async (patch: Partial<PaginationQueryParams>) => {
  const nextQueryParams = normalizeQueryParams({
    ...selectionStore.queryParams,
    ...patch,
  })

  selectionStore.queryParams = nextQueryParams
  await syncRouteQuery(nextQueryParams)
  await nextTick()
  await fetchSelections()
}

const handleSearch = async (search: string) => {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

const handleClear = async () => {
  selectionStore.queryParams = { ...defaultQueryParams }
  await syncRouteQuery(defaultQueryParams)
  await nextTick()
  await fetchSelections()
}

const handleSortChange = async (sort: string) => {
  await updateQueryParams({ sort, page: 1 })
}

const handleLimitChange = async (limit: number) => {
  await updateQueryParams({ limit, page: 1 })
}

const handlePageChange = async (page: number) => {
  await updateQueryParams({ page })
}

const handleLoaded = (entriesFound: number) => {
  resultCount.value = entriesFound
}

const handleCreateSelection = async () => {
  try {
    const { responseCode } = await selectionStore.fetchTemplate()

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load selection template', { responseCode })

      return
    }

    selectionData.value = cloneReactiveData(selectionStore.template ?? {})
    dialogDisplayType.value = DialogDisplayType.CREATE
    isDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load selection template', error)
  }
}

const openSelectionDialog = async (selectionGUID: string, displayType: DialogDisplayTypeValue) => {
  try {
    const { responseCode } = await selectionStore.fetch(selectionGUID)

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load selection', { responseCode, selectionGUID })

      return
    }

    selectionData.value = cloneReactiveData(selectionStore.selection ?? {})
    dialogDisplayType.value = displayType
    isDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load selection', error)
  }
}

const handleEditSelection = async (selectionGUID: string) => {
  await openSelectionDialog(selectionGUID, DialogDisplayType.EDIT)
}

const handleViewSelection = async (selectionGUID: string) => {
  await openSelectionDialog(selectionGUID, DialogDisplayType.VIEW)
}

const handleSaveSelection = async (newSelectionData: SelectionData) => {
  try {
    const response =
      dialogDisplayType.value === DialogDisplayType.CREATE
        ? await selectionStore.create(newSelectionData, false)
        : await selectionStore.update(newSelectionData, false)

    const expectedCode =
      dialogDisplayType.value === DialogDisplayType.CREATE ? ResponseCode.CREATED : ResponseCode.OK

    if (response.responseCode !== expectedCode) {
      log.error('Failed to save selection', { responseCode: response.responseCode })

      return
    }

    await fetchSelections()
  } catch (error) {
    log.error('Failed to save selection', error)
  }
}

selectionStore.queryParams = { ...defaultQueryParams }
applyRouteQuery()
isRouteQueryReady.value = true

onMounted(fetchSelections)

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQuery()) return

    await nextTick()
    await fetchSelections()
  },
  { deep: true }
)
</script>

<template>
  <main class="selections-workspace">
    <VCard class="selections-workspace__hero">
      <VCardItem>
        <div class="selections-workspace__hero-content">
          <div>
            <span class="selections-workspace__eyebrow">Booking configuration</span>
            <VCardTitle class="selections-workspace__title"> Booking option selections </VCardTitle>
            <VCardSubtitle class="selections-workspace__subtitle">
              Review customer-facing selections, product relationships, and display guidance.
            </VCardSubtitle>
          </div>
          <div class="selections-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>

      <VDivider />

      <VCardActions class="selections-workspace__actions">
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreateSelection"
        >
          New selection
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchSelections"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>

    <SelectionListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="selectionStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
      @refresh="fetchSelections"
    />

    <BookingOptionSelectionsDatatable
      ref="selectionsDataTable"
      @edit="handleEditSelection"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
      @view="handleViewSelection"
    />
  </main>

  <BookingOptionSelectionDialog
    v-if="selectionData"
    v-model:is-dialog-visible="isDialogVisible"
    :booking-option-selection-data="selectionData"
    :display-type="dialogDisplayType"
    @create="handleSaveSelection"
    @update="handleSaveSelection"
  />
</template>

<style scoped lang="scss">
.selections-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.selections-workspace__hero {
  overflow: hidden;
  min-inline-size: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.selections-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.selections-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.selections-workspace__title {
  overflow: visible;
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
  text-overflow: clip;
  white-space: normal;
}

.selections-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.selections-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.selections-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.selections-workspace__metric strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1.5rem;
  font-weight: 590;
  letter-spacing: -0.04em;
}

.selections-workspace__actions {
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.selections-workspace__actions :deep(.v-btn) {
  min-block-size: 2.5rem;
  border-radius: 6px;
}

@media (max-width: 599px) {
  .selections-workspace {
    gap: 1rem;
  }

  .selections-workspace__hero-content {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }

  .selections-workspace__metric {
    inline-size: 100%;
  }

  .selections-workspace__actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    padding: 0.75rem;
  }

  .selections-workspace__actions :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
